import { useEffect, useState, useCallback } from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/**
 * Hook para instalação do app como PWA.
 * - Em Android/Desktop (Chrome/Edge): captura o evento `beforeinstallprompt` e
 *   permite instalação nativa com 1 clique.
 * - Em iOS (Safari): mostra instruções (Compartilhar → Adicionar à Tela de Início).
 * - Detecta se já está instalado para esconder o botão.
 */
export const useInstallApp = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    // Detecta iOS
    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Detecta se já está instalado (rodando standalone)
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone === true;
    setIsInstalled(standalone);

    // No iOS, a Web Share API abre o menu nativo de compartilhamento,
    // onde o usuário encontra "Adicionar à Tela de Início".
    const shareAvailable =
      iOS &&
      typeof navigator !== "undefined" &&
      !!navigator.share &&
      !!navigator.canShare &&
      navigator.canShare({ url: window.location.href });
    setCanShare(shareAvailable);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const installedHandler = () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return null;
    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setDeferredPrompt(null);
    }
    return choice.outcome;
  }, [deferredPrompt]);

  const openShare = useCallback(async () => {
    if (!canShare) return false;
    try {
      await navigator.share({
        title: document.title,
        url: window.location.href,
      });
      return true;
    } catch (err) {
      // AbortError = usuário fechou o menu; não consideramos falha.
      if ((err as Error)?.name === "AbortError") return true;
      return false;
    }
  }, [canShare]);

  // Pode mostrar botão se: tem prompt nativo OU é iOS (precisa instruções) — e não está instalado
  const canInstall = !isInstalled && (deferredPrompt !== null || isIOS);

  return {
    canInstall,
    isInstalled,
    isIOS,
    install,
    openShare,
    canShare,
    hasNativePrompt: deferredPrompt !== null,
  };
};
